import {CloseButton, createStyles, Tooltip} from '@mantine/core';
import classNames from 'classnames';
import React, {useCallback, useEffect, useRef, useState} from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import useResizeObserver from 'ui-library/hooks/useResizeObserver';

interface PrimitiveTagsInputTagProps {
  label: string;
  onRemove: (event: React.MouseEvent) => void;
  disabled?: boolean;
  readOnly?: boolean;
  className?: string;
}

const useStyles = createStyles((theme, params: {disabled: boolean}) => ({
  tag: {
    display: 'flex',
    alignItems: 'center',
    height: '20px',
    maxWidth: 'calc(100% - 10px)',
    paddingLeft: theme.spacing.xxs,
    paddingRight: params.disabled ? theme.spacing.xs : theme.spacing.none,
    fontWeight: 500,
    fontSize: theme.fontSizes.sm,
    borderRadius: theme.radius.sm,
    backgroundColor: params.disabled ? theme.colors.gray[3] : theme.colors.gray[1],
    color: theme.colors.gray[7],
    cursor: params.disabled ? 'not-allowed' : 'default',
    userSelect: 'none',
  },
  // Mirrors Mantine's own `DefaultValue` tag: an ellipsis-truncating label. Its flex-item minimum width
  // resolves to 0 (per the flexbox spec, because `overflow` here is not `visible`), so it shrinks and
  // truncates rather than forcing the tag to grow past the input's width.
  label: {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  removeButton: {
    marginLeft: '1px',
    color: theme.colors.gray[7],
  },
}));

// TODO(BNCH-163953) Replace with Mantine v8 TagsInput
/**
 * Renders a single committed tag, replacing Mantine's own `MultiSelect` tag/value component so we can wrap
 * the label in a tooltip revealing the full value whenever it's actually clipped by the ellipsis truncation —
 * otherwise long values are permanently unreadable once they no longer fit.
 */
export default function PrimitiveTagsInputTag(props: PrimitiveTagsInputTagProps): JSX.Element {
  const {classes} = useStyles({disabled: Boolean(props.disabled)});
  const [isTruncated, setIsTruncated] = useState(false);
  const labelRef = useRef<HTMLSpanElement | null>(null);

  const checkTruncation = useCallback((): void => {
    const label = labelRef.current;
    setIsTruncated(label != null && label.scrollWidth > label.clientWidth);
  }, []);

  const {ref: resizeRef} = useResizeObserver<HTMLSpanElement>({onResize: checkTruncation});
  const setLabelRef = useCallback(
    (node: HTMLSpanElement | null) => {
      resizeRef(node);
      labelRef.current = node;
    },
    [resizeRef]
  );

  useEffect(() => {
    checkTruncation();
  }, [props.label, checkTruncation]);

  const label = (
    <span ref={setLabelRef} className={classes.label}>
      {props.label}
    </span>
  );

  return (
    <div className={classNames(classes.tag, props.className)}>
      {isTruncated ? (
        <Tooltip label={props.label} withinPortal={true}>
          {label}
        </Tooltip>
      ) : (
        label
      )}
      {!props.disabled && !props.readOnly && (
        <CloseButton
          aria-hidden={true}
          onMouseDown={props.onRemove}
          size={22}
          radius={2}
          color="blue"
          variant="transparent"
          iconSize="70%"
          className={classes.removeButton}
          tabIndex={-1}
          {...getTestProps({
            component: 'PrimitiveTagsInputTag',
            element: 'removeButton',
            key: props.label,
          })}
        />
      )}
    </div>
  );
}
